import captionService from "CaptionService";
import { parseDate } from "DateExtensions";
import { DateTimeType } from "DateTimeConstants";
import dialogService from "DialogService";
import { getHumanReadableName } from "EntityCalculatedPropertyExtensions";
import { getInterfaceName, getPrimaryKey } from "EntityExtensions";
import { FormFlowAbortError } from "Errors";
import type { FormFlowUI } from "FormFlowUITypes";
import { loadTemplateAsync } from "ModuleLoader";
import { NotificationType } from "NotificationType";
import RuleService from "RuleService";
import semaphoreProvider, { type SemaphoreUsage } from "SemaphoreProvider";
import { EntityState } from "breeze-client";

export async function registerEntityUsageAsync(formFlowUI: FormFlowUI): Promise<void> {
  const entity = formFlowUI.entity?.();
  if (
    entity &&
    entity.entityAspect.entityState !== EntityState.Added &&
    RuleService.get(getInterfaceName(entity)).userActivityNotification()
  ) {
    const usages = await acquireSemaphoreHandleAsync(formFlowUI, getPrimaryKey(entity));
    if (usages.length !== 0) {
      const humanReadableName = await getHumanReadableName(entity).loadAsync();
      const header = captionService.getString(
        "f047f7e1-4086-42f5-8b75-b0d611cf4b47",
        "These users are currently modifying {0} - {1}:",
        humanReadableName?.caption,
        humanReadableName?.name,
      );
      const usageMessages = usages.map((usage) => {
        return {
          usageMessage: captionService.getString(
            "8eb0fedb-b543-450f-b97b-8e8aa8d6d1b5",
            "{0} in {1} session(s) since {2}",
            usage.UserFullName,
            usage.UsageCount,
            parseDate(usage.CreateDateTimeUTC, DateTimeType.DateTimeUtc).format("D/MM/YYYY h:mm:ss A"),
          ),
        };
      });

      await dialogService.showDialogAsync({
        viewModel: { header, usageMessages },
        title: captionService.getString(
          "1b8b2fe1-cb15-451a-9bcb-46622f49c912",
          "Another session is editing the same information",
        ),
        notificationType: NotificationType.Information,
        bodyAllowHtml: true,
        /*! SuppressStringValidation Usages.ejs is a file */
        bodyDeferred: loadTemplateAsync("Usages.ejs"),
      });
    }
  }
}

async function acquireSemaphoreHandleAsync(formFlowUI: FormFlowUI, entityPK: string): Promise<SemaphoreUsage[]> {
  if (formFlowUI.isAborted) {
    throw new FormFlowAbortError();
  }

  if (formFlowUI.semaphoreTokens.has(entityPK)) {
    return [];
  }

  const semaphoreHandle = await semaphoreProvider.acquireHandleAsync(entityPK);
  if (!semaphoreHandle) {
    return [];
  }

  if (semaphoreHandle.Token) {
    formFlowUI.semaphoreTokens.set(entityPK, semaphoreHandle.Token);
  }

  return semaphoreHandle.Usages;
}
